// ===========================================
// PREFERENCES SLICE - User Preferences
// ===========================================

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Language, Currency } from '@/types';
import { appConfig } from '@/config';
import { storageService, STORAGE_KEYS } from '@/services/external/storage';

interface PreferencesState {
  // Language
  language: Language;

  // Currency
  currency: Currency;

  // Theme
  theme: 'light' | 'dark' | 'system';

  // Display preferences
  itemsPerPage: number;
  gridView: boolean;

  // Notifications
  emailNotifications: boolean;
  pushNotifications: boolean;

  // Cookie consent
  cookieConsent: boolean;
  analyticsConsent: boolean;
  marketingConsent: boolean;
}

// Load initial state from storage
const loadInitialState = (): PreferencesState => {
  const storedLanguage = storageService.get<Language>(STORAGE_KEYS.LANGUAGE);
  const storedCurrency = storageService.get<Currency>(STORAGE_KEYS.CURRENCY);
  const storedTheme = storageService.get<'light' | 'dark' | 'system'>(STORAGE_KEYS.THEME);
  const cookieConsent = storageService.get<boolean>(STORAGE_KEYS.COOKIE_CONSENT);

  return {
    language: storedLanguage || appConfig.defaultLanguage,
    currency: storedCurrency || appConfig.defaultCurrency,
    theme: storedTheme || 'system',
    itemsPerPage: 12,
    gridView: true,
    emailNotifications: true,
    pushNotifications: false,
    cookieConsent: cookieConsent || false,
    analyticsConsent: false,
    marketingConsent: false,
  };
};

const initialState: PreferencesState = loadInitialState();

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    // Language
    setLanguage: (state, action: PayloadAction<Language>) => {
      state.language = action.payload;
      storageService.set(STORAGE_KEYS.LANGUAGE, action.payload);
    },

    // Currency
    setCurrency: (state, action: PayloadAction<Currency>) => {
      state.currency = action.payload;
      storageService.set(STORAGE_KEYS.CURRENCY, action.payload);
    },

    // Theme
    setTheme: (state, action: PayloadAction<'light' | 'dark' | 'system'>) => {
      state.theme = action.payload;
      storageService.set(STORAGE_KEYS.THEME, action.payload);
    },

    // Display
    setItemsPerPage: (state, action: PayloadAction<number>) => {
      state.itemsPerPage = action.payload;
    },
    setGridView: (state, action: PayloadAction<boolean>) => {
      state.gridView = action.payload;
    },

    // Notifications
    setEmailNotifications: (state, action: PayloadAction<boolean>) => {
      state.emailNotifications = action.payload;
    },
    setPushNotifications: (state, action: PayloadAction<boolean>) => {
      state.pushNotifications = action.payload;
    },

    // Cookie consent
    setCookieConsent: (state, action: PayloadAction<boolean>) => {
      state.cookieConsent = action.payload;
      storageService.set(STORAGE_KEYS.COOKIE_CONSENT, action.payload);
    },
    setAnalyticsConsent: (state, action: PayloadAction<boolean>) => {
      state.analyticsConsent = action.payload;
    },
    setMarketingConsent: (state, action: PayloadAction<boolean>) => {
      state.marketingConsent = action.payload;
    },
    setAllConsents: (
      state,
      action: PayloadAction<{ cookie: boolean; analytics: boolean; marketing: boolean }>
    ) => {
      state.cookieConsent = action.payload.cookie;
      state.analyticsConsent = action.payload.analytics;
      state.marketingConsent = action.payload.marketing;
      storageService.set(STORAGE_KEYS.COOKIE_CONSENT, action.payload.cookie);
    },

    // Reset preferences
    resetPreferences: (state) => {
      const defaults = loadInitialState();
      Object.assign(state, defaults);
    },
  },
});

export const {
  setLanguage,
  setCurrency,
  setTheme,
  setItemsPerPage,
  setGridView,
  setEmailNotifications,
  setPushNotifications,
  setCookieConsent,
  setAnalyticsConsent,
  setMarketingConsent,
  setAllConsents,
  resetPreferences,
} = preferencesSlice.actions;

export const preferencesReducer = preferencesSlice.reducer;

// Selectors
export const selectLanguage = (state: { preferences: PreferencesState }) => state.preferences.language;
export const selectCurrency = (state: { preferences: PreferencesState }) => state.preferences.currency;
export const selectTheme = (state: { preferences: PreferencesState }) => state.preferences.theme;
export const selectItemsPerPage = (state: { preferences: PreferencesState }) => state.preferences.itemsPerPage;
export const selectGridView = (state: { preferences: PreferencesState }) => state.preferences.gridView;
export const selectCookieConsent = (state: { preferences: PreferencesState }) => state.preferences.cookieConsent;
export const selectPreferences = (state: { preferences: PreferencesState }) => state.preferences;
